import _t from 'i18n!nls/user-account';

/**
 * The minimum length required for a user's name.
 *
 * @constant
 * @type {number}
 */
export const USER_NAME_MIN_LENGTH = 1;

/**
 * The maximum length allowed for a user's name.
 *
 * @constant
 * @type {number}
 */
export const USER_NAME_MAX_LENGTH = 128;

/**
 * The maximum length allowed for an email address.
 *
 * @constant
 * @type {number}
 */
export const EMAIL_MAX_LENGTH = 75;

/**
 * Regular expression pattern for validating a user's name.
 *
 * This pattern ensures that the user's name length is between `USER_NAME_MIN_LENGTH` and `USER_NAME_MAX_LENGTH`.
 *
 * @constant
 * @type {string}
 */
export const USER_NAME_REGEX_PATTERN = `.{${USER_NAME_MIN_LENGTH},${USER_NAME_MAX_LENGTH}}`;

/**
 * The minimum length required for a user's password.
 *
 * @constant
 * @type {number}
 */
export const USER_PASSWORD_MIN_LENGTH = 8;

/**
 * The maximum length allowed for a user's password.
 *
 * @constant
 * @type {number}
 */
export const USER_PASSWORD_MAX_LENGTH = 72;

/**
 * Regular expression pattern for validating a user's password.
 *
 * This pattern ensures that the password length is between `USER_PASSWORD_MIN_LENGTH` and `USER_PASSWORD_MAX_LENGTH`.
 *
 * @constant
 * @type {string}
 */
export const USER_PASSWORD_REGEX_PATTERN = `.{${USER_PASSWORD_MIN_LENGTH},${USER_PASSWORD_MAX_LENGTH}}`;

/**
 * Regular expression pattern for validating email addresses.
 *
 * This pattern ensures that the email contains at least one character before the '@' symbol,
 * followed by at least one character, a dot, and at least one more character.
 *
 * @constant
 * @type {string}
 */
export const USER_EMAIL_REGEX_PATTERN = '.+@.+\\..+';

/**
 * @deprecated Use the single source of truth for modes inside
 * the [authentication constants file](https://github.com/webedx-spark/web/blob/5ab0a8d2fc6f08c912cd2c308b0c174d267e0997/static/bundles/authentication/constants.tsx#L256),
 * which is planned to be moved to a shared package in the future.
 */ export const SIGNUP_FORM_KEY = 'signup';
/**
 * @deprecated Use the single source of truth for modes inside
 * the [authentication constants file](https://github.com/webedx-spark/web/blob/5ab0a8d2fc6f08c912cd2c308b0c174d267e0997/static/bundles/authentication/constants.tsx#L256),
 * which is planned to be moved to a shared package in the future.
 */ export const LOGIN_FORM_KEY = 'login';

/**
 * @deprecated Use the single source of truth for modes inside
 * the [authentication constants file](https://github.com/webedx-spark/web/blob/5ab0a8d2fc6f08c912cd2c308b0c174d267e0997/static/bundles/authentication/constants.tsx#L256),
 * which is planned to be moved to a shared package in the future.
 */
export const AUTH_MODES = {
  signup: 'signup',
  signupEmbedded: 'signup_embedded',
  login: 'login',
  expired: 'expired',
} as const;

export const AUTH_FORM_IDS = {
  name: 'name',
  email: 'email',
  password: 'password',
  verificationCode: 'verificationCode',
} as const;

export const getFormTranslations = () => ({
  AUTH: _t('Authentication'),
  SLOGAN: _t('Learn skills from top universities for free.'),
  COMPLETE: _t('Complete'),
  LOGIN: _t('Log in'),
  SIGN_UP: _t('Sign up'),
  SIGN_UP_WITH_ORG: _t('Sign up with your organization'),
  SIGN_IN_WITH_ORG: _t('Log in with your organization'),
  OR: _t('or'),
  HAVING_TROUBLE: _t('Having trouble logging in?'),
});

export type SIGNUP_FORM_KEY_TYPE = 'signup';
export type LOGIN_FORM_KEY_TYPE = 'login';
export type COMPLETE_FORM_KEY_TYPE = 'complete';
export type LINK_FORM_KEY_TYPE = 'linked';

/**
 * @deprecated Use the single source of truth for modes type inside
 * the [authentication constants file](https://github.com/webedx-spark/web/blob/5ab0a8d2fc6f08c912cd2c308b0c174d267e0997/static/bundles/authentication/constants.tsx#L271),
 * which is planned to be moved to a shared package in the future.
 */
export type AuthMode = SIGNUP_FORM_KEY_TYPE | LOGIN_FORM_KEY_TYPE | COMPLETE_FORM_KEY_TYPE | LINK_FORM_KEY_TYPE;

/**
 * @deprecated This type is only used within the university-invitation bundle. Please move the type to that bundle.
 */
export type UserObjectType = {
  id: number;
  fullName: string;
  // eslint-disable-next-line camelcase
  email_address: string;
  authenticated: boolean;
};

enum MODES {
  COMPLETE = 'complete',
  LINK = 'link',
  LOGIN = 'login',
  SIGNUP = 'signup',
  EXPIRED = 'expired',
}

export { MODES };
